import { useLazyQuery, useMutation } from '@apollo/client';
import { userSelector } from 'common/globalComponents/selectors';
import TableError from 'components/TableError';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { createStructuredSelector } from 'reselect';
import {
  ACTIVE_AND_DELETED_ARRAY,
  FOOD_ITEM_REF_SOURCES,
  LANGUAGE_ARRAY_WITH_STRING,
  PRINT_LANGUAGE,
  PRINT_LANGUAGE_COLOR,
  SORT_VALUES_DATE,
} from 'utils/constants';
import { SMKLinks } from 'utils/links';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Loader from '../../../components/Loader';
import Paginate from '../../../components/Paginate';
import { useRouter } from '../../../hooks/useRouter';
import { buildURL, checkIfValueExist, getImageUrl, IMAGE_SIZES, titleCaseIfExists } from '../../../utils/helper';
import { DELETE_FOOD_ITEMS, UPDATE_FOOD_ITEMS } from '../graphql/food_item.mutation';
import { FOOD_ITEMS_SEARCH } from '../graphql/food_item.query';

const stateSelector = createStructuredSelector({
  user: userSelector,
});

const FoodItemsList = () => {
  const { user } = useSelector(stateSelector);

  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const [foodItemsList, setFoodItemsList] = useState([]);
  const [foodItemsListPagination, setFoodItemsListPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    language: LANGUAGE_ARRAY_WITH_STRING[0],
    name: '',
    refSource: null,
  };
  const [filters, setFilters] = useState(defaultFilters);

  const refSourcesList = Object.keys(FOOD_ITEM_REF_SOURCES)?.map((e) => ({
    label: e?.toUpperCase(),
    value: FOOD_ITEM_REF_SOURCES[e],
  }));

  const [getFoodItemsListRequest, { loading: getFoodItemsListLoading }] = useLazyQuery(FOOD_ITEMS_SEARCH, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_foodItem_search)) {
        setFoodItemsList(data?.admin_nutrition_foodItem_search?.results);
        setFoodItemsListPagination(data?.admin_nutrition_foodItem_search?.pagination);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [markActiveInActiveRequest, { loading: markActiveInActiveLoading }] = useMutation(UPDATE_FOOD_ITEMS, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_foodItem_update)) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Food Item has been updated.`);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const [deleteFoodItemRequest, { loading: deleteFoodItemtLoading }] = useMutation(DELETE_FOOD_ITEMS, {
    onCompleted: (data) => {
      handleChangePage(parseInt(router?.query?.page, 10) || 1);
      toast.success(`Success! Food Item has been deleted.`);
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = async (page) => {
    setPageInUrl(page);

    await getFoodItemsListRequest({
      variables: {
        searchDTO: {
          page,
          size: 20,
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          language: checkIfValueExist(filters?.language?.value),
          name: filters?.name,
          refSource: filters?.refSource,
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = async () => {
    setPageInUrl(1);

    setFilters({ ...defaultFilters, name: '', refSource: null });
    await getFoodItemsListRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          ...defaultFilters,
          active: checkIfValueExist(defaultFilters?.active?.value),
          deleted: checkIfValueExist(defaultFilters?.deleted?.value),
          language: checkIfValueExist(defaultFilters?.language?.value),
          name: '',
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  const handleActive = async (data) => {
    await markActiveInActiveRequest({
      variables: {
        id: data?.id,
        foodItemDTO: {
          name: data?.name,
          active: !data?.active,
        },
      },
    });
  };

  const handleDelete = async (data) => {
    const result = await confirm({
      title: `${data?.deleted ? 'Undelete' : 'Delete'} Food Item`,
      message: `Are you sure, you want to ${data?.deleted ? 'Undelete' : 'Delete'} this food item?`,
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });

    if (result) {
      if (!data?.deleted) {
        await deleteFoodItemRequest({
          variables: {
            id: data?.id,
          },
        });
      } else {
        await markActiveInActiveRequest({
          variables: {
            id: data?.id,
            foodItemDTO: {
              name: data?.name,
              active: false,
              deleted: !data.deleted,
            },
          },
        });
      }
    }
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  }, []);

  return (
    <>
      <div className="page-content">
        <Loader isActive={getFoodItemsListLoading || markActiveInActiveLoading || deleteFoodItemtLoading} />

        <Container fluid>
          <Breadcrumbs title="Recipe" breadcrumbItem="Food Items list" />

          <Card>
            <CardBody>
              <div className="mt-4">
                <div className="row">
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Active</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.active}
                      onChange={(value) => setFilters({ ...filters, active: value })}
                    />
                  </div>
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Deleted</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.deleted}
                      onChange={(value) => setFilters({ ...filters, deleted: value })}
                    />
                  </div>
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Language</label>
                    <Select
                      options={LANGUAGE_ARRAY_WITH_STRING}
                      value={filters.language}
                      onChange={(value) => setFilters({ ...filters, language: value })}
                    />
                  </div>

                  <div className="col-lg-2 mb-4">
                    <label htmlFor="name" className="form-label">
                      Ref Source
                    </label>
                    <Select
                      options={refSourcesList}
                      placeholder="Select source..."
                      value={
                        refSourcesList?.find((e) => e?.value?.toLowerCase() === filters.refSource?.toLowerCase()) ??
                        null
                      }
                      onChange={(e) => setFilters({ ...filters, refSource: e.value })}
                    />
                  </div>

                  <div className="col-lg-4 mb-4">
                    <label htmlFor="name" className="form-label">
                      Name
                    </label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Name..."
                      value={filters.name}
                      onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-2">
                <a className="btn btn-primary me-4" onClick={applyFilters}>
                  Apply Filters
                </a>
                <a className="" onClick={clearFilters}>
                  Clear
                </a>
              </div>
            </CardBody>
          </Card>

          <Row className="mb-2 align-items-center">
            <Col sm="4">
              <div className="search-box me-2 mb-2 d-inline-block">
                <div className="position-relative font-bold">
                  {foodItemsListPagination?.totalCount} Food Items found
                </div>
              </div>
            </Col>

            <Col sm="8">
              <div className="text-sm-end">
                <Link
                  replace
                  className="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2"
                  to={SMKLinks.FOOD_ITEMS_ADD}>
                  <i className="mdi mdi-plus me-1" />
                  Add Food Items
                </Link>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="table-responsive" style={{ minHeight: 300 }}>
                <Table className="project-list-table   table-centered table-borderless">
                  <thead>
                    <tr className="table-warning">
                      <th scope="col">S.No.</th>
                      <th scope="col" style={{ width: '40px' }}>
                        Image
                      </th>
                      <th scope="col">Name</th>
                      <th scope="col">Type</th>
                      <th scope="col">Ref Source</th>
                      <th scope="col">Language</th>
                      <th scope="col">is Allergen</th>
                      <th scope="col">Active</th>
                      <th scope="col">Deleted</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(foodItemsList) ? (
                      foodItemsList?.map((data, index) => {
                        return (
                          <tr key={`_test_${data.name}_`} className={!data?.active ? 'bg-danger' : ''}>
                            <td>{index + 1}</td>
                            <td>
                              {!isEmpty(data?.image) && (
                                <img
                                  src={getImageUrl(data?.image, IMAGE_SIZES.ORIGINAL)}
                                  className="img-fluid rounded-circle"
                                  style={{ width: '40px', height: '40px' }}
                                />
                              )}
                            </td>
                            <td>
                              {
                                <Link
                                  to={buildURL(SMKLinks.FOOD_ITEMS_EDIT, {
                                    id: data?.id,
                                  })}>
                                  {data?.name}
                                </Link>
                              }
                              <br />
                              {data?.slug}
                            </td>
                            <td>{titleCaseIfExists(data?.type?.name?.replaceAll('-', ' '))}</td>
                            <td>{data?.refSource?.toUpperCase()}</td>
                            <td>
                              <Badge className={PRINT_LANGUAGE_COLOR[data?.language]}>
                                {PRINT_LANGUAGE[data?.language]}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.isAllergen ? 'bg-success' : 'bg-danger'}>
                                {data?.isAllergen ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.active ? 'bg-success' : 'bg-danger'}>
                                {data?.active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.deleted ? 'bg-success' : 'bg-danger'}>
                                {data?.deleted ? 'Yes' : 'No'}
                              </Badge>
                            </td>

                            <td>
                              {user.entityPermissions?.nutrition_recipe?.update && (
                                <UncontrolledDropdown>
                                  <DropdownToggle href="#" className="card-drop" tag="i">
                                    <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    <DropdownItem onClick={() => handleActive(data)}>
                                      <i
                                        className={`fas  ${
                                          data?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                                        }   m-1 pe-3`}
                                      />
                                      {data?.active ? 'Disable' : 'Enable'}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleDelete(data)}>
                                      <i className={'fas fa-trash-alt text-danger m-1 pe-3'} />
                                      {data?.deleted ? 'Undelete' : 'Delete'}
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <TableError />
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <Paginate pageInfo={foodItemsListPagination} onChangePage={handleChangePage} />
        </Container>
      </div>
    </>
  );
};

export default FoodItemsList;
